import store from "../store";
import api from "../services";
import { hasAnalyticsConsent } from "./consent";

let gtmInitialized = false;
let gaInitialized = false;

const PURCHASE_ANALYTICS_KEY = "glovelerPurchaseAnalytics";

const getActiveConfig = () => {
  const { currentConfig, defaultConfig } = store.state.config;
  return currentConfig || defaultConfig || {};
};

const normalizeId = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
};

export const getGoogleTagManagerId = (config = getActiveConfig()) =>
  normalizeId(config?.googleTagManagerId);

export const getGoogleAnalyticsMeasurementId = (config = getActiveConfig()) =>
  normalizeId(config?.googleAnalyticsMeasurementId);

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

const gtag = function gtag() {
  ensureDataLayer();
  window.dataLayer.push(arguments);
};

window.gtag = window.gtag || gtag;

const injectScript = (src, attributes = {}) => {
  if (document.querySelector(`script[src="${src}"]`)) {
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  Object.entries(attributes).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });
  document.head.appendChild(script);
};

const loadGoogleTagManager = (containerId) => {
  if (!containerId || gtmInitialized) {
    return;
  }

  ensureDataLayer();
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  injectScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);

  if (!document.getElementById("gtm-noscript")) {
    const noscript = document.createElement("noscript");
    noscript.id = "gtm-noscript";
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
      containerId,
    )}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  gtmInitialized = true;
};

const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || gaInitialized) {
    return;
  }

  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
  );
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });
  gaInitialized = true;
};

export const isGoogleAnalyticsReady = () =>
  Boolean(gtmInitialized || gaInitialized);

export const initGoogleAnalytics = (config = getActiveConfig()) => {
  if (!hasAnalyticsConsent(config)) {
    return;
  }

  const gtmId = getGoogleTagManagerId(config);
  const measurementId = getGoogleAnalyticsMeasurementId(config);

  if (gtmId) {
    loadGoogleTagManager(gtmId);
  }

  // Direct GA4 only when GTM is not used (avoid double-counting the same stream).
  if (measurementId && !gtmId) {
    loadGoogleAnalytics(measurementId);
  }
};

const getGaClientAndSession = () => {
  const gaCookie = document.cookie.match(/_ga=([^;]+)/);
  if (!gaCookie) {
    return { clientId: null, sessionId: null };
  }
  const parts = gaCookie[1].split(".");
  const clientId = parts.slice(-2).join(".");
  const sessionId = parseInt(parts.slice(-2)[0], 10);
  return {
    clientId: clientId || null,
    sessionId: Number.isFinite(sessionId) ? sessionId : null,
  };
};

const hasAnalyticsConfigured = (config = getActiveConfig()) =>
  Boolean(getGoogleTagManagerId(config) || getGoogleAnalyticsMeasurementId(config));

export const pushDataLayer = (event, payload = {}) => {
  ensureDataLayer();
  window.dataLayer.push({
    event,
    ...payload,
  });
};

/**
 * Dual-channel event: GTM dataLayer + Measurement Protocol via backend (secret stays private).
 */
export const trackAnalyticsEvent = async (event, params = {}, config = getActiveConfig()) => {
  if (!event || !hasAnalyticsConfigured(config) || !hasAnalyticsConsent(config)) {
    return;
  }

  pushDataLayer(event, params);

  const measurementId = getGoogleAnalyticsMeasurementId(config);
  if (!measurementId) {
    return;
  }

  try {
    const { clientId, sessionId } = getGaClientAndSession();
    await api.post("/analytics/event", {
      event,
      params,
      ...(clientId ? { clientId } : {}),
      ...(sessionId != null ? { sessionId } : {}),
    });
  } catch (_error) {
    // Analytics must never break booking flow.
  }
};

const cartLinePrice = (item) => {
  const guests = item?.guests;
  if (Array.isArray(guests)) {
    let price = 0;
    guests.forEach((guest) => {
      price += Number(guest.price) || 0;
      (guest.variations || []).forEach((variation) => {
        price += (Number(variation.quantity) || 0) * (Number(variation.totalAmount) || 0);
      });
    });
    return Number(price.toFixed(2));
  }
  const quantity = Number(item?.quantity) || 1;
  const unitPrice = Number(item?.price) || 0;
  return Number((unitPrice * quantity).toFixed(2));
};

const cartLineQuantity = (item) => {
  if (Array.isArray(item?.guests)) {
    return item.guests.length || 1;
  }
  return Number(item?.quantity) || 1;
};

const cartLineName = (item) =>
  item?.product?.productDescription?.name ||
  item?.product?.sellableUnit?.names?.[0]?.text ||
  item?.product?.rate?.boardName ||
  item?.product?.roomType?.name ||
  item?.product?.name ||
  "Room";

export const buildAnalyticsItems = (checkoutData = {}) => {
  const propertyName = checkoutData?.property?.name || "";
  const propertyId = String(checkoutData?.propertyId || checkoutData?.property?.id || "");
  const items = [];

  (checkoutData.cart || []).forEach((item) => {
    items.push({
      item_name: cartLineName(item),
      item_id: String(item?.id || item?.product?.id || ""),
      affiliation: propertyName,
      property_id: propertyId,
      price: cartLinePrice(item),
      quantity: cartLineQuantity(item),
    });
  });

  (checkoutData.extras || []).forEach((extra) => {
    const quantity = Number(extra?.quantity) || 0;
    if (quantity <= 0) {
      return;
    }
    const unitPrice = Number(extra?.price) || 0;
    items.push({
      item_name:
        extra?.extra?.productDescription?.name ||
        extra?.productDescription?.name ||
        extra?.name ||
        "Extra",
      item_id: String(extra?.id || ""),
      affiliation: propertyName,
      property_id: propertyId,
      price: Number((unitPrice * quantity).toFixed(2)),
      quantity,
    });
  });

  return items;
};

const nightsBetween = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) {
    return 0;
  }
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = end.getTime() - start.getTime();
  if (!Number.isFinite(diff) || diff <= 0) {
    return 0;
  }
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const guestAgeCounts = (checkoutData = {}) => {
  let babyCount = 0;
  let childCount = 0;
  (checkoutData.cart || []).forEach((item) => {
    (item.guests || []).forEach((guest) => {
      (guest.variations || []).forEach((variation) => {
        const quantity = Number(variation.quantity) || 0;
        const maxAge = Number(variation.maxAge);
        if (!quantity || !Number.isFinite(maxAge)) {
          return;
        }
        if (maxAge <= 2) {
          babyCount += quantity;
        } else if (maxAge <= 17) {
          childCount += quantity;
        }
      });
    });
  });
  return { babyCount, childCount };
};

export const trackCheckoutSelection = async (checkoutData = {}) => {
  const items = buildAnalyticsItems(checkoutData);
  if (!items.length) {
    return;
  }

  const currency = checkoutData.cart?.[0]?.currencyCode || "";
  const value = items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
  const property = checkoutData.property || {};

  await trackAnalyticsEvent("view_item", {
    items,
    currency,
    value,
    hotelName: property.name || "",
    hotel_code: property.id || "",
  });

  for (const item of items.filter((entry) => entry.item_name !== "Extra")) {
    await trackAnalyticsEvent("room_count", {
      room_name: item.item_name,
      room_count: item.quantity,
      productCode: item.item_id,
    });
  }

  await trackAnalyticsEvent("booking_price", {
    booking_price: Number(value.toFixed(2)),
  });

  if (checkoutData.promoCode) {
    await trackAnalyticsEvent("promo_code", {
      promo_code: checkoutData.promoCode,
    });
  }

  const { babyCount, childCount } = guestAgeCounts(checkoutData);
  if (babyCount > 0) {
    await trackAnalyticsEvent("baby_count", { baby_count: babyCount });
  }
  if (childCount > 0) {
    await trackAnalyticsEvent("child_count", { child_count: childCount });
  }
};

export const trackUpsellProduct = async (extra = {}) => {
  const quantity = Number(extra.quantity) || 0;
  if (quantity <= 0) {
    return;
  }
  await trackAnalyticsEvent("upsell_products", {
    title: extra?.productDescription?.name || extra?.name || "Extra",
    currency: extra.currencyCode || "",
    quantity,
    unitPrice: Number(extra.price) || 0,
  });
};

export const trackCompleteBooking = async (checkoutData = {}, totalValue) => {
  const items = buildAnalyticsItems(checkoutData);
  const rooms = items
    .filter((item) => item.item_name !== "Extra")
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const value =
    totalValue != null
      ? Number(totalValue) || 0
      : items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

  await trackAnalyticsEvent("complete_booking", {
    languageSelect: localStorage.getItem("locale") || "en",
    hotelName: checkoutData?.property?.name || "",
    productName: items.map((item) => item.item_name).join(", "),
    totalValue: value,
    numberOfRooms: rooms,
  });
};

export const stashPurchaseAnalytics = (payload) => {
  try {
    sessionStorage.setItem(PURCHASE_ANALYTICS_KEY, JSON.stringify(payload));
  } catch (_error) {
    // ignore storage failures
  }
};

export const buildPurchaseAnalyticsPayload = ({
  checkoutData = {},
  transactionId,
  value,
  currency,
} = {}) => {
  const items = buildAnalyticsItems(checkoutData);
  const rooms = items
    .filter((item) => item.item_name !== "Extra")
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const computedValue =
    value != null
      ? Number(value) || 0
      : items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

  return {
    transactionId,
    value: computedValue,
    currency: currency || checkoutData.cart?.[0]?.currencyCode || "",
    checkIn: checkoutData?.date?.checkIn || "",
    checkOut: checkoutData?.date?.checkOut || "",
    rooms,
    items,
  };
};

export const trackPurchaseConfirmation = async () => {
  let payload = null;
  try {
    const raw = sessionStorage.getItem(PURCHASE_ANALYTICS_KEY);
    if (raw) {
      payload = JSON.parse(raw);
      sessionStorage.removeItem(PURCHASE_ANALYTICS_KEY);
    }
  } catch (_error) {
    payload = null;
  }

  if (!payload?.transactionId || !hasAnalyticsConfigured() || !hasAnalyticsConsent()) {
    return;
  }

  const ecommerce = {
    transaction_id: String(payload.transactionId),
    value: Number(payload.value) || 0,
    currency: payload.currency || "",
    checkin_date: payload.checkIn || "",
    checkout_date: payload.checkOut || "",
    room_nights: nightsBetween(payload.checkIn, payload.checkOut),
    rooms: payload.rooms || 0,
    items: payload.items || [],
  };

  // GTM purchase only — Measurement Protocol purchase is sent server-side after booking.
  pushDataLayer("purchase", { ecommerce });

  await trackAnalyticsEvent("booking_finished", { booking_finished: true });
  await trackAnalyticsEvent("booking_confirmed", { booking_confirmed: true });
  await trackAnalyticsEvent("booking_number", {
    booking_number: payload.transactionId,
  });
};

export const trackGoogleAnalyticsPageView = (config = getActiveConfig()) => {
  if (!hasAnalyticsConfigured(config) || !hasAnalyticsConsent(config)) {
    return;
  }

  const path = `${window.location.pathname}${window.location.search}`;
  const title = document.title;
  const measurementId = getGoogleAnalyticsMeasurementId(config);

  ensureDataLayer();
  window.dataLayer.push({
    event: "page_view",
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });

  if (measurementId && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
      send_to: measurementId,
    });
  }
};
